'use client';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { Faq } from '@/components/Faq';
import { ImageComponent } from '@/components/Image';
import { ThemeMode } from '@/helpers/enums';
import { Modals } from '@/helpers/modals';
import { someGroupMember, USER_GROUP, vipGroups } from '@/helpers/user';
import { useTimeoutRegistrationModal } from '@/hooks/timeoutRegistrationModal';
import { ReceiveBonusesNotice } from '@/modules/common/components/ReceiveBonusesNotice';
import { useGetCommonStore, useModalsStore } from '@/store';
import { ScContainer } from '@/styles/common';
import { useQuestPromo } from '../../hooks';
import { Mission } from './components/Mission';
import {
  ScDailyBlock,
  ScFaq,
  ScQuestHeader,
  ScQuestHeaderAssets,
  ScQuestPage,
  ScTerms,
} from './styled';

export const groupMemberWithoutSilverVIP = someGroupMember(
  vipGroups.filter((vipGroup) => vipGroup !== USER_GROUP.SILVER)
);

const QuestPage: FC = () => {
  const { t } = useTranslation('quest');
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const open = useModalsStore.use.open();

  useTimeoutRegistrationModal();

  const { missions, isVipOrBonusHunter } = useQuestPromo();

  const handleOpenRegistration = () => {
    open(Modals.Registration);
  };

  return (
    <ScQuestPage>
      <ScQuestHeader>
        <ScContainer>
          <div className="bonuses-notice-box">
            <ReceiveBonusesNotice
              successMessage="canIssueBonuses.success"
              translation="promotions"
              warningMessage="canIssueBonuses.warning"
            />
          </div>
          <div className="content">
            <h1 className="title">{t('pageTitle')}</h1>
            <h1 className="description">{t('description')}</h1>
          </div>
        </ScContainer>

        <ScDailyBlock isAuthenticated={isAuthenticated}>
          <div className="missions-wrapp">
            {missions.map((item) => (
              <Mission
                isVipOrBonusHunter={isVipOrBonusHunter}
                key={item.date}
                {...item}
              />
            ))}
            <div className="top">
              <ImageComponent
                alt="decoration"
                fill
                src={
                  isMobileDevice
                    ? '/quest-xmas/grid-decor-top-mob.webp'
                    : '/quest-xmas/grid-decor-top-desk.webp'
                }
              />
            </div>
            <div className="bottom">
              <ImageComponent
                alt="decoration"
                fill
                src={
                  isMobileDevice
                    ? '/quest-xmas/grid-decor-bottom-mob.webp'
                    : '/quest-xmas/grid-decor-bottom-desk.webp'
                }
              />
            </div>
          </div>

          {!isAuthenticated && (
            <Button
              className="regButton"
              onClick={handleOpenRegistration}
              primary
              wide
            >
              {t('common:button.register')}
            </Button>
          )}
        </ScDailyBlock>

        <ScQuestHeaderAssets>
          <ImageComponent
            alt="background"
            className="bg"
            fill
            src={
              isMobileDevice
                ? '/quest-xmas/bg-mobile3.webp'
                : '/quest-xmas/bg3.webp'
            }
          />
        </ScQuestHeaderAssets>
      </ScQuestHeader>

      <ScFaq>
        <div className="content">
          <h3>{t('quest:faq.title')}</h3>
          <Faq
            itemClassName="faq-item"
            mode={ThemeMode.Dark}
            ns="quest"
            translationKey="faq.questions"
          />
        </div>
        <div className="assets">
          <div className="decorationContainer">
            <ImageComponent
              alt="decor"
              className="decoration"
              fill
              src={
                isMobileDevice
                  ? '/quest-xmas/terms-decoration-mob.webp'
                  : '/quest-xmas/terms-decoration.webp'
              }
            />
          </div>
        </div>
      </ScFaq>

      <ScTerms isAuthenticated={isAuthenticated}>
        <div className="content">
          <h3>{t('quest:terms.title')}</h3>
          <ol>
            <li>{t('quest:terms.list.1')}</li>
            <li>{t('quest:terms.list.2')}</li>
            <li>{t('quest:terms.list.3')}</li>
            <li>{t('quest:terms.list.4')}</li>
            <li>{t('quest:terms.list.5')}</li>
            <li>{t('quest:terms.list.6')}</li>
            <li>{t('quest:terms.list.7')}</li>
            <li>
              {isVipOrBonusHunter
                ? t('quest:terms.list.8Vip')
                : t('quest:terms.list.8')}
            </li>
          </ol>
        </div>
        <div className="assets">
          <ImageComponent
            alt="decor"
            className="animation"
            fill
            src={'/quest-xmas/terms-animation.gif'}
          />
        </div>
      </ScTerms>
    </ScQuestPage>
  );
};

export default QuestPage;
